import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import CommonModal from "../../components/shared/modalLayout/CommonModal";
import InputWithLabel from "../../components/shared/fields/InputWithLabel";
import SelectBox from "../../components/shared/fields/SelectBox ";
import Button from "../../components/shared/buttons/button";
import useTranslator from "../../hooks/useTranslator";
import { useUpdatePastingLogMutation } from "./mixingApi";
import { getCurrentDate } from "../../helpers/Utils";
import { PASTING_BOX_WEIGHT_IN_POUND, POUND_TO_KG } from "../../helpers/constants";

const initialForm = {
  date:                   getCurrentDate("YYYY-MM-DD"),
  batch_code:             "",
  total_weight:           "",
  packed_weight:          "",
  unpacked_weight:        "",
  total_boxes:            "",
  lot_completion_percent: "",
  moisture_percent:       "",
  water_activity:         "",
  status:                 "mixing",
};

const PastingModal = ({ show, setShow, record, mode = "edit" }) => {
  const { _t } = useTranslator();
  const [formData, setFormData] = useState(initialForm);
  const [updatePastingLog, { isLoading: isUpdating }] = useUpdatePastingLogMutation();

  const STATUS_OPTIONS = [
    { value: "mixing", label: _t("status_mixing", "Mixing") },
    { value: "packed", label: _t("status_packed", "Packed") },
  ];

  const isView = mode === "view";

  useEffect(() => {
    if (show && record) {
      setFormData({
        date:                   record.date                   || getCurrentDate("YYYY-MM-DD"),
        batch_code:             record.batch_code             || "",
        total_weight:           record.total_weight           || "",
        packed_weight:          record.packed_weight          || "",
        unpacked_weight:        record.unpacked_weight        || "",
        total_boxes:            record.total_boxes            || "",
        lot_completion_percent: record.lot_completion_percent || "",
        moisture_percent:       record.moisture_percent       || "",
        water_activity:         record.water_activity         || "",
        status:                 record.status                 || "mixing",
      });
    }
  }, [show, record]);

  // Auto-calc lot_completion_percent
  useEffect(() => {
    const total  = parseFloat(formData.total_weight)  || 0;
    const packed = parseFloat(formData.packed_weight) || 0;
    const pct = total > 0 ? parseFloat(((packed / total) * 100).toFixed(2)) : 0;
    setFormData((p) => ({ ...p, lot_completion_percent: pct }));
  }, [formData.packed_weight, formData.unpacked_weight, formData.total_weight]);

  // Auto-calc packed/unpacked from total_boxes
  useEffect(() => {
    const total_weight   = parseFloat(formData.total_weight) || 0;
    const total_boxes    = parseFloat(formData.total_boxes)  || 0;
    const packed_weight  = total_boxes * (PASTING_BOX_WEIGHT_IN_POUND * POUND_TO_KG);
    // const unpacked_weight = total_weight - packed_weight;
    setFormData((p) => ({
      ...p,
      packed_weight:   parseFloat(packed_weight).toFixed(2),
      // unpacked_weight: parseFloat(unpacked_weight).toFixed(2),
    }));
  }, [formData.total_boxes, formData.total_weight]);

  const handleClose = () => setShow(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => {
      const updated = { ...p, [name]: value };
      const total    = parseFloat(updated.total_weight)    || 0;
      const packed   = parseFloat(updated.packed_weight)   || 0;
      const unpacked = parseFloat(updated.unpacked_weight) || 0;
      // if (name === "packed_weight") {
      //   const max = total - (parseFloat(p.unpacked_weight) || 0);
      //   if (packed > max) updated.packed_weight = max >= 0 ? String(max) : "0";
      // }
      // if (name === "unpacked_weight") {
      //   const max = total - (parseFloat(p.packed_weight) || 0);
      //   if (unpacked > max) updated.unpacked_weight = max >= 0 ? String(max) : "0";
      // }

      if (name === "packed_weight") {
        const max = total - (parseFloat(p.unpacked_weight) || 0);

        if (packed > max) {
          updated.packed_weight = max >= 0
            ? Number(max).toFixed(2)
            : "0.00";
        }
      }

      if (name === "unpacked_weight") {
        const max = total - (parseFloat(p.packed_weight) || 0);

        if (unpacked > max) {
          updated.unpacked_weight = max >= 0
            ? Number(max).toFixed(2)
            : "0.00";
        }
      }
      
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isUpdating) return;
    const payload = {
      ...formData,
      total_weight:           parseFloat(formData.total_weight)           || 0,
      packed_weight:          parseFloat(formData.packed_weight)          || 0,
      unpacked_weight:        parseFloat(formData.unpacked_weight)        || 0,
      total_boxes:            parseFloat(formData.total_boxes)            || 0,
      lot_completion_percent: parseFloat(formData.lot_completion_percent) || 0,
      moisture_percent:       parseFloat(formData.moisture_percent)       || 0,
      water_activity:         parseFloat(formData.water_activity)         || 0,
    };
    try {
      await updatePastingLog({ id: record.id, requestBody: payload }).unwrap();
      handleClose();
    } catch (error) {
      console.error("Error saving pasting log:", error);
    }
  };

  return (
    <CommonModal
      show={show}
      handleClose={handleClose}
      setShow={setShow}
      title={isView ? _t("view_pasting_log", "View Pasting Log") : _t("edit_pasting_log", "Edit Pasting Log")}
    >
      <Row>
        <Col lg={6}><InputWithLabel label={_t("date", "Date")} type="date" name="date" value={formData.date} onChange={handleChange} disabled={true} /></Col>
        <Col lg={6}><InputWithLabel label={_t("batch_code", "Batch Code")} type="text" name="batch_code" value={formData.batch_code} onChange={handleChange} disabled={true} /></Col>
        <Col lg={6}><InputWithLabel label={_t("total_weight", "Total Weight")} type="number" name="total_weight" value={formData.total_weight} onChange={handleChange} placeholder="0" disabled={true} /></Col>
        <Col lg={6}><InputWithLabel label={_t("total_boxes", "Total Boxes")} type="number" name="total_boxes" value={formData.total_boxes} onChange={handleChange} placeholder="0" disabled={isView} /></Col>
        <Col lg={6}><InputWithLabel label={_t("packed_weight", "Packed Weight")} type="number" name="packed_weight" value={formData.packed_weight} onChange={handleChange} placeholder="0" disabled={isView} /></Col>
        <Col lg={6}><InputWithLabel label={_t("unpacked_weight", "Unpacked Weight")} type="number" name="unpacked_weight" value={formData.unpacked_weight} onChange={handleChange} placeholder="0" disabled={isView} /></Col>
        <Col lg={6}><InputWithLabel label={_t("moisture_percent", "Moisture %")} type="number" name="moisture_percent" value={formData.moisture_percent} onChange={handleChange} placeholder="0" disabled={isView} /></Col>
        <Col lg={6}><InputWithLabel label={_t("water_activity", "Water Activity")} type="number" name="water_activity" value={formData.water_activity} onChange={handleChange} placeholder="0" disabled={isView} /></Col>
        <Col lg={6}><InputWithLabel label={_t("lot_completion_percent", "Lot Completion %")} type="number" name="lot_completion_percent" value={formData.lot_completion_percent} onChange={handleChange} placeholder="0" disabled={true} /></Col>
        <Col lg={6}><SelectBox label={_t("status", "Status")} name="status" value={formData.status} onChange={handleChange} options={STATUS_OPTIONS} disabled={isView} /></Col>
      </Row>
      {!isView && (
        <Row>
          <Col className="text-end">
            <Button label={_t("save_changes", "Save Changes")} size="small" onClick={handleSubmit} disabled={isUpdating} />
          </Col>
        </Row>
      )}
    </CommonModal>
  );
};

export default PastingModal;
